import { type FC, useEffect } from "react";
import { Alert, Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Stream, StreamContent } from "@/partner/entities/streams";
import { useStreamClipsQuery } from "@/partner/entities/streams";
import { StreamClipList } from "./StreamClipList";
import { StreamStudioLive } from "./StreamStudioLive";
import { StreamStudioRecording } from "./StreamStudioRecording";
import { StreamDialogHeader } from "./StreamDialogHeader";
import { StreamRecordingTrimStatus } from "./StreamRecordingTrimStatus";

interface StreamStudioTabProps {
  content: StreamContent;
  stream: Stream;
  onClose: () => void;
}

const CLIPS_POLL_INTERVAL_MS = 30000;

export const StreamStudioTab: FC<StreamStudioTabProps> = ({
  content,
  stream,
  onClose,
}) => {
  const { t } = useTranslation();
  // Без записи Cloudflare физически не может отдать фрагмент — не запрашиваем
  // список клипов вовсе, когда запись выключена.
  const { data, refetch, startPolling, stopPolling } = useStreamClipsQuery({
    variables: { contentId: content.id },
    skip: !stream.enableRecord,
  });

  // Клип обрабатывается на стороне Cloudflare — список обновляется сам,
  // как ClipList в референсе (там был поллинг раз в 30с). Останавливаем
  // поллинг, когда каждый клип уже либо готов, либо окончательно упал
  // (transcodingError) — иначе вкладка тянет сеть вечно за клипом,
  // который никогда не станет readyToStream. Управляем поллингом через
  // startPolling/stopPolling, а не через опцию pollInterval: она вычисляется
  // до того, как хук вернёт data, то есть сослаться там на data нельзя.
  const hasPendingClips = (data?.streamClips ?? []).some(
    (clip) =>
      !clip.content.video?.readyToStream && !clip.content.transcodingError
  );

  useEffect(() => {
    if (!stream.enableRecord || !hasPendingClips) {
      stopPolling();
      return;
    }

    startPolling(CLIPS_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [hasPendingClips, stream.enableRecord, startPolling, stopPolling]);

  // Fixed-хедер рендерит каждая вкладка сама (см. StreamForm); у Studio его
  // не было, и при переходе на вкладку пропадали заголовок и крестик.
  const header = (
    <StreamDialogHeader
      title={t("streams.form.dialogTitle")}
      onClose={onClose}
    />
  );

  if (!stream.enableRecord) {
    return (
      <>
        {header}
        <Box sx={{ p: 3 }}>
          <Alert severity="info">{t("streams.studio.recordDisabled")}</Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      {header}
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          {stream.status === "STARTED" && stream.liveConnected ? (
            <StreamStudioLive
              content={content}
              stream={stream}
              onCreated={() => void refetch()}
            />
          ) : stream.status === "FINISHED" && stream.recordPlayback?.hlsUrl ? (
            <StreamStudioRecording
              content={content}
              stream={stream}
              onCreated={() => void refetch()}
            />
          ) : stream.status === "FINISHED" ? (
            <Alert severity="info">
              {t("streams.studio.recordProcessing")}
            </Alert>
          ) : (
            <Alert severity="info">{t("streams.studio.notStartedYet")}</Alert>
          )}
          {/* Под записью: идёт ли обрезка до окна эфира или она не удалась. */}
          {stream.status === "FINISHED" && (
            <StreamRecordingTrimStatus stream={stream} sx={{ mt: 1 }} />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <StreamClipList
            clips={data?.streamClips ?? []}
            onChanged={() => void refetch()}
          />
        </Grid>
      </Grid>
    </>
  );
};
